import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  #pendingUpdates = 0;
  #confirmedCount = 0;
  #restoreController = null;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
    this.#confirmedCount = this.currentCartCount || 0;
    if (document.readyState === 'complete' && performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
      this.onPageShow({ persisted: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    this.#restoreController?.abort();
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = async (event) => {
    if (!event.persisted && performance.getEntriesByType('navigation')[0]?.type !== 'back_forward') return;
    if (this.#pendingUpdates || this.#restoreController) return;
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map(element => element.dataset.sectionId))].slice(0, 5);
    if (!sectionIds.length) return;
    const controller = new AbortController();
    this.#restoreController = controller;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.set('sections', sectionIds.join(','));
      const response = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        signal: AbortSignal.any([controller.signal, AbortSignal.timeout(6000)]),
      });
      if (!response.ok) throw new Error('Cart restoration failed');
      const sections = await response.json();
      let restoredCart;
      for (const html of Object.values(sections)) {
        if (typeof html !== 'string') continue;
        const encoded = new DOMParser().parseFromString(html, 'text/html')
          .querySelector('cart-items-component[data-cart-state]')?.getAttribute('data-cart-state');
        if (!encoded) continue;
        try {
          const cart = JSON.parse(encoded);
          if (Array.isArray(cart.items) && Number.isSafeInteger(cart.item_count) && cart.item_count >= 0 &&
              cart.items.every(item => Number.isSafeInteger(item.quantity) && item.quantity >= 0) &&
              cart.items.reduce((sum, item) => sum + item.quantity, 0) === cart.item_count) {
            restoredCart = cart;
            break;
          }
        } catch {}
      }
      if (!restoredCart) throw new Error('Cart restoration returned invalid state');
      if (controller.signal.aborted || !this.isConnected) return;
      this.#restoreController = null;
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: 'update', context: 'cart', lines: [],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(restoredCart),
          detail: { sections, items: restoredCart.items, itemCount: restoredCart.item_count, source: 'cart-history-restore', didError: false },
        }),
      }));
    } catch (error) {
      if (!controller.signal.aborted) console.warn('[cart-icon] Could not restore cart:', error);
    } finally {
      if (this.#restoreController === controller) this.#restoreController = null;
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    if (!event.promise) return;
    this.#restoreController?.abort();
    this.#restoreController = null;
    const quantity = event.action === 'add'
      ? event.lines?.reduce((total, line) => total + Number(line.quantity || 0), 0)
      : 0;
    if (this.#pendingUpdates === 0) {
      this.#confirmedCount = this.currentCartCount || 0;
      if (Number.isSafeInteger(quantity) && quantity > 0) {
        this.renderCartBubble(this.#confirmedCount + quantity, true, false);
      }
    }
    this.#pendingUpdates++;
    this.setAttribute('aria-busy', 'true');
    event.promise
      .then(({ cart }) => {
        const itemCount = cart?.totalQuantity;
        if (Number.isSafeInteger(itemCount) && itemCount >= 0) {
          this.#confirmedCount = itemCount;
        }
        this.renderCartBubble(this.#confirmedCount);
      })
      .catch((error) => {
        this.renderCartBubble(this.#confirmedCount, false);
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      })
      .finally(() => {
        this.#pendingUpdates--;
        if (this.#pendingUpdates === 0) this.removeAttribute('aria-busy');
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   * @param {boolean} [persist=true] - Whether this is a confirmed cart count.
   */
  renderCartBubble = async (itemCount, animate = true, persist = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    if (persist) try { sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    ); } catch {}

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    let sessionStorageCount;
    try { sessionStorageCount = sessionStorage.getItem('cart-count'); } catch { return; }

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
